import { useState, useEffect } from 'react'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { useWorkspaceStore } from '@/store/workspaceStore'
import {
  ChevronDown,
  ChevronRight,
  Database,
  ArrowRightLeft,
  Leaf,
  ArrowLeft,
  Loader2,
  AlertCircle,
  MapPin,
  ExternalLink,
  Tag,
} from 'lucide-react'
import { cn } from '@/lib/utils'
import { lcaAPI } from '@/lib/api/client'
import type { ActivityWithExchanges, ProjectExchange } from '@/lib/types/project-graph'

interface BackgroundDatasetViewProps {
  activities: ActivityWithExchanges[]
  projectDatabase?: string
}

// Response types from the activity details endpoint
interface ActivityDetailsResponse {
  activity_code: string
  database_name: string
  basic_info: {
    name: string
    unit: string
    location: string
    reference_product: string
    activity_type: string
  }
  description: {
    process_description: string | null
    product_description: string | null
    classification: string | null
    synonyms: string[]
  }
  exchanges_summary: {
    technosphere_inputs: number
    reference_outputs: number
    biosphere_flows: number
  }
  external_links: {
    ecoquery_url: string | null
  }
  technosphere_inputs?: TechnosphereInput[]
  biosphere_flows?: BiosphereFlow[]
}

interface TechnosphereInput {
  product_name: string
  product_code: string
  unit: string
  amount: number | null
  supplier_name: string | null
  supplier_location: string | null
  supplier_code: string | null
}

interface BiosphereFlow {
  name: string
  code: string
  compartment: string | null
  sub_compartment: string | null
  unit: string
  amount: number | null
  type: 'emission' | 'resource'
}

export function BackgroundDatasetView({ activities, projectDatabase }: BackgroundDatasetViewProps) {
  const { selectedNodeId, selectedNodeType, setRightTab } = useWorkspaceStore()

  const selectedExchange =
    selectedNodeType === 'exchange' && selectedNodeId
      ? activities.flatMap((a) => a.exchanges).find((e) => e.id === selectedNodeId)
      : null

  const isEcoinventExchange =
    selectedExchange &&
    selectedExchange.input_database &&
    !selectedExchange.input_database.startsWith('project:') &&
    selectedExchange.input_database !== 'custom'

  if (!selectedExchange) {
    return (
      <div className="p-4 text-center text-muted-foreground">
        <Database className="h-12 w-12 mx-auto mb-3 opacity-20" />
        <p className="text-sm">Select an exchange to view its background data</p>
        <p className="text-xs mt-1">Click on an Ecoinvent input in the Activity tab</p>
      </div>
    )
  }

  if (!isEcoinventExchange) {
    return (
      <div className="p-4 text-center text-muted-foreground">
        <Database className="h-12 w-12 mx-auto mb-3 opacity-20" />
        <p className="text-sm">Custom exchange selected</p>
        <p className="text-xs mt-1">Background data is only available for Ecoinvent exchanges</p>
        <Button
          variant="outline"
          size="sm"
          className="mt-3 gap-1.5"
          onClick={() => setRightTab('activity')}
        >
          <ArrowLeft className="h-3 w-3" />
          Back to Activity
        </Button>
      </div>
    )
  }

  return <DatasetDetails exchange={selectedExchange} projectDatabase={projectDatabase} />
}

interface DatasetDetailsProps {
  exchange: ProjectExchange
  projectDatabase?: string
}

function DatasetDetails({ exchange, projectDatabase }: DatasetDetailsProps) {
  const { setRightTab } = useWorkspaceStore()
  const [details, setDetails] = useState<ActivityDetailsResponse | null>(null)
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const databaseName = exchange.input_database || projectDatabase
  const activityCode = exchange.input_activity_code

  useEffect(() => {
    if (!activityCode || !databaseName) return

    let cancelled = false
    setIsLoading(true)
    setError(null)

    lcaAPI
      .getActivityDetails(activityCode, databaseName, true)
      .then((res) => {
        if (!cancelled) setDetails(res.data)
      })
      .catch((err) => {
        if (!cancelled) {
          const msg =
            err.response?.data?.detail?.message ||
            err.response?.data?.detail ||
            'Failed to load activity details'
          setError(typeof msg === 'string' ? msg : JSON.stringify(msg))
        }
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false)
      })

    return () => {
      cancelled = true
    }
  }, [activityCode, databaseName])

  return (
    <div className="divide-y divide-border">
      {/* Header with back button */}
      <div className="p-3 space-y-2">
        <div className="flex items-center gap-2">
          <Button
            variant="ghost"
            size="sm"
            className="h-6 px-2 gap-1 text-xs"
            onClick={() => setRightTab('activity')}
          >
            <ArrowLeft className="h-3 w-3" />
            Activity
          </Button>
        </div>

        <div>
          <Badge variant="outline" className="mb-2 text-[10px] gap-1">
            <Database className="h-2.5 w-2.5" />
            {exchange.input_database}
          </Badge>
          <h4 className="text-sm font-semibold">{details?.basic_info.name || exchange.name}</h4>
          {details?.basic_info.location && (
            <div className="flex items-center gap-1 text-xs text-muted-foreground mt-1">
              <MapPin className="h-3 w-3" />
              {details.basic_info.location}
            </div>
          )}
          {activityCode && (
            <p className="text-xs text-muted-foreground font-mono mt-1">{activityCode}</p>
          )}
        </div>
      </div>

      {/* Exchange reference info */}
      <div className="p-3">
        <p className="text-[10px] font-medium uppercase tracking-wide text-muted-foreground mb-2">
          Used in your model
        </p>
        <div className="flex items-center justify-between text-xs bg-muted/50 rounded-md p-2">
          <span>Amount</span>
          <span className="font-mono font-medium">
            {exchange.amount} {exchange.unit}
          </span>
        </div>
      </div>

      {isLoading ? (
        <div className="p-8 text-center">
          <Loader2 className="h-6 w-6 mx-auto animate-spin text-muted-foreground" />
          <p className="text-xs text-muted-foreground mt-2">Loading dataset details...</p>
        </div>
      ) : error ? (
        <div className="p-4 text-center text-destructive">
          <AlertCircle className="h-6 w-6 mx-auto mb-2" />
          <p className="text-xs">{error}</p>
          <Button
            variant="outline"
            size="sm"
            className="mt-2 text-xs"
            onClick={() => {
              if (activityCode && databaseName) {
                setIsLoading(true)
                setError(null)
                lcaAPI
                  .getActivityDetails(activityCode, databaseName, true)
                  .then((res) => setDetails(res.data))
                  .catch(() => setError('Failed to load activity details'))
                  .finally(() => setIsLoading(false))
              }
            }}
          >
            Retry
          </Button>
        </div>
      ) : details ? (
        <>
          <DatasetMetadata details={details} />

          <ExchangeSection
            title="Technosphere Inputs"
            icon={ArrowRightLeft}
            iconColor="text-blue-500"
            count={details.exchanges_summary.technosphere_inputs}
            items={details.technosphere_inputs}
            renderItem={(item: TechnosphereInput) => (
              <div className="flex items-start justify-between gap-2 py-1.5 px-2 rounded hover:bg-accent/30 transition-colors">
                <div className="min-w-0 flex-1">
                  <p className="text-xs font-medium truncate">{item.product_name}</p>
                  {item.supplier_name && (
                    <p className="text-[10px] text-muted-foreground truncate">
                      {item.supplier_name}
                      {item.supplier_location ? ` (${item.supplier_location})` : ''}
                    </p>
                  )}
                </div>
                {item.amount != null && (
                  <span className="text-[10px] font-mono text-muted-foreground whitespace-nowrap">
                    {formatAmount(item.amount)} {item.unit}
                  </span>
                )}
              </div>
            )}
          />

          <ExchangeSection
            title="Biosphere Flows"
            icon={Leaf}
            iconColor="text-green-500"
            count={details.exchanges_summary.biosphere_flows}
            items={details.biosphere_flows}
            renderItem={(item: BiosphereFlow) => (
              <div className="flex items-start justify-between gap-2 py-1.5 px-2 rounded hover:bg-accent/30 transition-colors">
                <div className="min-w-0 flex-1">
                  <p className="text-xs font-medium truncate">{item.name}</p>
                  <p className="text-[10px] text-muted-foreground truncate">
                    {[item.compartment, item.sub_compartment].filter(Boolean).join(' → ')}
                    {item.type === 'resource' && (
                      <span className="ml-1 text-amber-500">(resource)</span>
                    )}
                  </p>
                </div>
                {item.amount != null && (
                  <span className="text-[10px] font-mono text-muted-foreground whitespace-nowrap">
                    {formatAmount(item.amount)} {item.unit}
                  </span>
                )}
              </div>
            )}
          />

          {/* Ecoquery link */}
          {details.external_links.ecoquery_url && (
            <div className="p-3">
              <a
                href={details.external_links.ecoquery_url}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-1.5 text-xs text-primary hover:underline"
              >
                <ExternalLink className="h-3 w-3" />
                View on ecoquery
              </a>
            </div>
          )}
        </>
      ) : null}
    </div>
  )
}

// --- Helper components ---

function DatasetMetadata({ details }: { details: ActivityDetailsResponse }) {
  const info = details.basic_info
  const desc = details.description
  const dbParts = details.database_name.split('-')
  const version = dbParts[1] || 'Unknown'
  const systemModel = dbParts[2] || 'Unknown'

  return (
    <div className="p-3 space-y-3">
      <p className="text-[10px] font-medium uppercase tracking-wide text-muted-foreground">
        Dataset Information
      </p>
      <div className="grid grid-cols-2 gap-2 text-xs">
        <MetadataItem label="Version" value={version} />
        <MetadataItem label="System Model" value={systemModel} />
        <MetadataItem label="Unit" value={info.unit} />
        <MetadataItem label="Reference Product" value={info.reference_product || '—'} />
        <MetadataItem label="Location" value={info.location} />
        <MetadataItem label="Activity Type" value={info.activity_type || '—'} />
      </div>

      {desc.classification && (
        <div className="text-xs">
          <div className="flex items-center gap-1 text-muted-foreground mb-1">
            <Tag className="h-3 w-3" />
            <span className="text-[10px] font-medium uppercase tracking-wide">Classification</span>
          </div>
          <p className="text-foreground">{desc.classification}</p>
        </div>
      )}

      {desc.synonyms && desc.synonyms.length > 0 && (
        <div className="text-xs">
          <p className="text-[10px] font-medium uppercase tracking-wide text-muted-foreground mb-1">
            Synonyms
          </p>
          <div className="flex flex-wrap gap-1">
            {desc.synonyms.map((s, i) => (
              <Badge key={i} variant="secondary" className="text-[10px]">
                {s}
              </Badge>
            ))}
          </div>
        </div>
      )}

      {desc.process_description && (
        <ExpandableText label="Process Description" text={desc.process_description} />
      )}

      {desc.product_description && (
        <ExpandableText label="Product Description" text={desc.product_description} />
      )}
    </div>
  )
}

function ExpandableText({ label, text }: { label: string; text: string }) {
  const [expanded, setExpanded] = useState(false)
  const isLong = text.length > 200

  return (
    <div className="text-xs">
      <p className="text-[10px] font-medium uppercase tracking-wide text-muted-foreground mb-1">
        {label}
      </p>
      <p className={cn('text-foreground leading-relaxed', !expanded && isLong && 'line-clamp-3')}>
        {text}
      </p>
      {isLong && (
        <button
          onClick={() => setExpanded(!expanded)}
          className="text-primary text-[10px] mt-0.5 hover:underline"
        >
          {expanded ? 'Show less' : 'Show more'}
        </button>
      )}
    </div>
  )
}

function MetadataItem({ label, value }: { label: string; value: string }) {
  return (
    <div className="bg-muted/30 rounded px-2 py-1.5">
      <p className="text-[10px] text-muted-foreground">{label}</p>
      <p className="font-medium truncate">{value}</p>
    </div>
  )
}

interface ExchangeSectionProps<T> {
  title: string
  icon: typeof ArrowRightLeft
  iconColor: string
  count: number
  items?: T[]
  renderItem: (item: T) => React.ReactNode
}

function ExchangeSection<T>({
  title,
  icon: Icon,
  iconColor,
  count,
  items,
  renderItem,
}: ExchangeSectionProps<T>) {
  const [isOpen, setIsOpen] = useState(false)

  return (
    <div>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center justify-between w-full px-3 py-2 hover:bg-accent/50 transition-colors"
      >
        <div className="flex items-center gap-2">
          {isOpen ? (
            <ChevronDown className="h-3.5 w-3.5 text-muted-foreground" />
          ) : (
            <ChevronRight className="h-3.5 w-3.5 text-muted-foreground" />
          )}
          <Icon className={cn('h-4 w-4', iconColor)} />
          <span className="text-xs font-medium">{title}</span>
          <Badge variant="outline" className="text-[10px] h-4 px-1.5">
            {count}
          </Badge>
        </div>
      </button>

      {isOpen && (
        <div className="px-2 pb-3">
          {items && items.length > 0 ? (
            <div className="space-y-0.5">
              {items.map((item, i) => (
                <div key={i}>{renderItem(item)}</div>
              ))}
            </div>
          ) : (
            <div className="rounded-md border border-dashed border-border p-3 text-center">
              <p className="text-xs text-muted-foreground">No exchanges found</p>
            </div>
          )}
        </div>
      )}
    </div>
  )
}

function formatAmount(value: number): string {
  if (Math.abs(value) >= 1000) return value.toFixed(1)
  if (Math.abs(value) >= 1) return value.toPrecision(4)
  if (Math.abs(value) >= 0.001) return value.toPrecision(3)
  return value.toExponential(2)
}
